/*
 * atc-guard.js — Add-To-Cart hardening (LT-02 / LT-03)
 *
 * Additive, isolated guard. Does NOT modify assets/theme.js (a minified bundle
 * with no source in this repo). The theme's productAddButtonForm() component is
 * an Alpine component whose `state` drives an `isLoading` getter and a
 * `:class="{'loading': isLoading}"` binding on [data-add-to-cart]. That state
 * is the source of truth this guard reads and, on timeout, resets.
 *
 * LT-03 — double-submit: the theme never disables the button during the async
 *   /cart/add.js call, so a fast double-tap fires two adds (duplicate qty).
 *   Fix: a capture-phase click guard blocks a second click while an add is in
 *   flight (loading class present, or a brief synchronous lock covering the
 *   gap before the theme applies that class).
 *
 * LT-02 — stuck spinner: on a network failure / non-200 the theme's error
 *   handler never clears the loading state, so the spinner spins forever with
 *   no retry. Fix: a watchdog started on the allowed click RESETS the Alpine
 *   component `state` to its INITIAL value after WATCHDOG_MS.
 *
 *   Why reset Alpine state rather than just strip the `loading` class: the
 *   component has no `:disabled` binding, so a stuck button still accepts
 *   clicks. Removing only the class leaves `state === LOADING`; because
 *   re-assigning LOADING is a no-op, Alpine never re-applies the class, so the
 *   guard's in-flight check is defeated and every later click silently fires
 *   another add (throttled only by the lock). Resetting `state` to INITIAL
 *   clears the spinner reactively AND restores a clean INITIAL->LOADING
 *   transition, so a retry is a single properly-guarded add. (Reported by
 *   Codex on PR #28; verified live.)
 *
 * Residual (inherent, rare): this cannot abort the theme's in-flight fetch, so
 * if a >WATCHDOG_MS request eventually SUCCEEDS after the user has retried, two
 * items can still land. The clean fix (reset in handleError + request abort)
 * belongs in the 8.1.1 source; this is the interim 7.3 guard.
 */
(function () {
  'use strict';

  var SEL = '[data-add-to-cart]';
  var LOADING = 'loading';
  var LOCK_MS = 1200;      // synchronous re-click lock: covers the gap before the theme sets `loading`
  var WATCHDOG_MS = 8000;  // still loading after this => the add hung/failed; reset so the user can retry

  // Alpine scope for the button, only if it actually carries the component `state`.
  function scopeOf(btn) {
    try {
      if (window.Alpine && typeof Alpine.$data === 'function') {
        var s = Alpine.$data(btn);
        if (s && 'state' in s) return s;
      }
    } catch (e) {}
    return null;
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    var btn = target && target.closest ? target.closest(SEL) : null;
    if (!btn) return;

    // LT-03: an add is already in flight — block this duplicate submit.
    if (btn.classList.contains(LOADING) || btn.__atcGuardLock) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    // Capture the component's INITIAL `state` sentinel once, before the theme
    // flips it to LOADING (this capture-phase listener runs first). Used as the
    // reset target below.
    if (btn.__atcInitState === undefined) {
      var s0 = scopeOf(btn);
      if (s0) btn.__atcInitState = s0.state;
    }

    // First (allowed) click. Lock briefly so an immediate second tap is blocked
    // even before the theme reactively applies the `loading` class.
    btn.__atcGuardLock = true;
    window.setTimeout(function () { btn.__atcGuardLock = false; }, LOCK_MS);

    // LT-02: watchdog. If still `loading` after WATCHDOG_MS, the add hung or
    // failed (the theme won't reset it). Reset the Alpine state to INITIAL so
    // the spinner clears AND the guard re-arms for a clean retry; fall back to
    // a DOM-only clear if the Alpine scope isn't reachable.
    window.setTimeout(function () {
      if (btn.classList.contains(LOADING)) {
        var scope = scopeOf(btn);
        if (scope && btn.__atcInitState !== undefined) {
          scope.state = btn.__atcInitState;
        } else {
          btn.classList.remove(LOADING);
          btn.removeAttribute('aria-busy');
        }
      }
      btn.__atcGuardLock = false;
    }, WATCHDOG_MS);
  }, true); // capture phase: runs before the theme's bubbling click handler
})();
